import type { Json } from '@/integrations/supabase/types';
import type { ProofKind, VisualDirection } from '@/lib/content-doctrine/piece';

export const CONCEPT_TASK_TYPE = 'creative-concept' as const;

export const CONCEPT_STATUSES = [
  'todo',
  'drafting',
  'review',
  'approved',
  'failed',
  'archived',
] as const;

export type ConceptStatus = (typeof CONCEPT_STATUSES)[number];

export const CONCEPT_DELIVERY_INTENTS = ['organic', 'paid'] as const;

export type ConceptDeliveryIntent = (typeof CONCEPT_DELIVERY_INTENTS)[number];

export const CONCEPT_FORMATS = ['single', 'carousel', 'video', 'text'] as const;

export type ConceptFormat = (typeof CONCEPT_FORMATS)[number] | null;

/** The only proof shape Task 7A permits the editor to persist. */
export type ConceptProof =
  | { kind: 'none'; ref: null; quote: null }
  | {
      kind: Exclude<ProofKind, 'none'>;
      ref: string | null;
      quote: string;
    };

/** The canonical plan-time image contract; never an uploaded/generated asset. */
export type ConceptVisualDirection = VisualDirection;

export const EMPTY_CONCEPT_PROOF: ConceptProof = {
  kind: 'none',
  ref: null,
  quote: null,
};

export const EMPTY_CONCEPT_VISUAL_DIRECTION: ConceptVisualDirection = {
  intent: 'none',
  subject: '',
  setting: '',
  negativeSpace: 'none',
  textPlan: { mode: 'none', headline: null },
  refusal: null,
};

export const NO_VISUAL_INTENT_LABEL = 'No visual intent recorded';
export const TYPOGRAPHIC_CARD_LABEL = 'Typographic card (no image)';

/**
 * The label for a Concept's visual intent. An intent of `none` with a planned
 * headline is a typographic card by design, not a missing field; only a Concept
 * with nothing planned reads as "no visual intent recorded".
 */
export function isTypographicCard(direction: ConceptVisualDirection): boolean {
  return direction.intent === 'none' && Boolean(direction.textPlan.headline?.trim());
}

export type ConceptFieldContract = {
  field: string;
  home: string;
  consumers: readonly string[];
};

/**
 * The persistence contract for Concept fields. A field does not belong in the
 * Concept model until at least one named consumer reads it.
 */
export const CONCEPT_FIELD_CONTRACT = [
  {
    field: 'Persona',
    home: 'project_tasks.audience_id',
    consumers: ['generation', 'grouping', 'provider targeting'],
  },
  {
    field: 'Angle, hook, proof, and CTA',
    home: 'project_tasks planning columns',
    consumers: ['generation', 'review'],
  },
  {
    field: 'Format',
    home: 'project_tasks.format',
    consumers: ['preview', 'handoff'],
  },
  {
    field: 'Delivery platform',
    home: 'project_task_delivery_intents.platform',
    consumers: ['intent-specific preview', 'provider handoff'],
  },
  {
    field: 'Visual direction',
    home: 'project_tasks.visual_direction',
    consumers: ['Studio generation'],
  },
  {
    field: 'References',
    home: 'project_files and grounding/proof',
    consumers: ['evidence display', 'generation'],
  },
  {
    field: 'Primary, headline, and description variants',
    home: 'ad_units.copy',
    consumers: ['paid preview', 'paid export'],
  },
  {
    field: 'Placement media',
    home: 'project_files.project_task_id and project_files.ad_placement',
    consumers: ['provider adapter', 'format-specific preview'],
  },
] as const satisfies readonly ConceptFieldContract[];

export const DEFERRED_CONCEPT_FIELDS = [
  'Expected filename',
  'Emotion',
  'Awareness',
  'Ad type',
  'Generic notes',
] as const;

export function getConceptDisplayLabel(
  intents: readonly ConceptDeliveryIntent[],
): 'Ad concept' | 'Post concept' | 'Creative concept' {
  const distinct = new Set(intents);

  if (distinct.size === 1 && distinct.has('paid')) return 'Ad concept';
  if (distinct.size === 1 && distinct.has('organic')) return 'Post concept';
  return 'Creative concept';
}
export type ConceptDeliveryIntentWrite = {
  project_task_id: string;
  intent: ConceptDeliveryIntent;
  platform: string | null;
  user_id: string;
  position: number;
};

export function buildConceptDeliveryIntentWrite(input: {
  projectTaskId: string;
  intent: ConceptDeliveryIntent;
  platform?: string | null;
  ownerId: string;
  position?: number;
}): ConceptDeliveryIntentWrite {
  return {
    project_task_id: input.projectTaskId,
    intent: input.intent,
    platform: input.platform?.trim() || null,
    user_id: input.ownerId,
    position: input.position ?? 0,
  };
}

export type AutoConceptPersistenceArgs = {
  p_run_id: string;
  p_source_ordinal: number;
  p_audience_id: string | null;
  p_platform: string | null;
  p_format: string | null;
  p_topic: string;
  p_hook: string;
  p_summary: string;
  p_cta: string;
  p_rationale: string | null;
  p_lane: string;
  p_angle_id: string | null;
  p_proof: Json;
  p_visual_direction: Json;
  p_grounding: Json;
  p_anatomy: Json;
};

/**
 * RPC payload for Auto-order. `p_run_id + p_source_ordinal` is the durable
 * retry key; platform stays on the paid intent and is never written to the
 * Concept task itself.
 */
export function buildAutoConceptPersistenceArgs(input: {
  runId: string;
  sourceOrdinal: number;
  audienceId: string | null;
  platform: string | null;
  format: string | null;
  topic: string;
  hook: string;
  summary: string;
  cta: string;
  rationale: string | null;
  lane: string;
  angleId: string | null;
  proof: Json;
  visualDirection: Json;
  grounding: Json;
  anatomy: Json;
}): AutoConceptPersistenceArgs {
  return {
    p_run_id: input.runId,
    p_source_ordinal: input.sourceOrdinal,
    p_audience_id: input.audienceId,
    p_platform: input.platform?.trim() || null,
    p_format: input.format,
    p_topic: input.topic,
    p_hook: input.hook,
    p_summary: input.summary,
    p_cta: input.cta,
    p_rationale: input.rationale,
    p_lane: input.lane,
    p_angle_id: input.angleId,
    p_proof: input.proof,
    p_visual_direction: input.visualDirection,
    p_grounding: input.grounding,
    p_anatomy: input.anatomy,
  };
}

// =============================================================================
// THE ONE DEFINITION of "this Concept has ad copy", and of which Concepts a
// Brief's counts are drawn from.
//
// It exists because two surfaces answered one question differently and shipped
// a contradiction the founder could see. Measured live 2026-08-27: `/briefs`
// said the Cold-brew Brief was `Ad copy 1/2`; `/briefs/2dd5b9f8-…` said `2/2`
// and painted the marks `--success`. The list was counting
// `project_tasks.current_ad_unit_id` — the SELECTION pointer the paid handoff
// reads — while the detail page counted whether any `ad_units` row exists. Two
// judges of one state must share their bar, and the terminal green fired on
// the one count the app itself disputed.
//
// THE BAR IS "an `ad_units` row exists". The selection pointer lost on three
// pieces of evidence, not on preference:
//   · The row is labelled `Ad copy`, not `Selected copy`. Drafted copy is copy.
//   · The Concept card directly beneath the detail rail already draws
//     `{approved}/{concept.adUnits.length}`, so a denominator of 1 is itself
//     proof an ad unit exists. Had the rail adopted the pointer, ONE viewport
//     would have carried three disagreeing answers instead of two.
//     ⚠️ That card row was labelled `Ad copy` too until 2026-08-27, and the
//     shared word was itself the third disagreement this bullet was worried
//     about: rail `Ad copy 1/1` (Concepts holding any copy) sat 330px from
//     card `Ad copy 0/3` (ad units approved) on a one-Concept Brief. The card
//     row is now `Approved` — same numbers, different question, different
//     word. `Ad copy` belongs to this predicate alone.
//   · Read-only SQL on `apegsjpotzvxumdedwty`, 2026-08-27: 1 of 35 Concepts in
//     the test account has `current_ad_unit_id` set; 6 hold real `ad_units`
//     rows. The pointer would have printed `Ad copy 0/N` on five Briefs that
//     demonstrably have copy — a false zero, a worse lie than the false green
//     it was proposed to cure.
//
// Guarded by `src/lib/briefs/__tests__/concept-copy-count.test.ts`, which pins
// both readers to these functions instead of asserting in a comment that they
// agree.
// =============================================================================

/** A Concept counts as having ad copy when at least one `ad_units` row exists
 *  for it. An unknown count (NaN — a relation that could not be read) is NOT a
 *  yes: an unread relation must never be reported as progress. */
export function conceptHasAdCopy(adUnitCount: number): boolean {
  return Number.isFinite(adUnitCount) && adUnitCount > 0;
}

/** Archived Concepts are outside every Brief quantity — the numerator AND the
 *  denominator. Counting them in one and not the other is how `2/3` appears
 *  beside a grid holding two cards. */
export function isActiveConcept(status: string | null | undefined): boolean {
  return status !== 'archived';
}

/** The count both the Brief list card and the Brief detail rail print as the
 *  numerator of `Ad copy N/M`. `adUnitCount` is supplied by the caller because
 *  the two readers hold different row shapes; the PREDICATE is shared, which is
 *  the part that drifted. */
export function countConceptsWithAdCopy<T>(
  concepts: readonly T[],
  adUnitCount: (concept: T) => number,
): number {
  return concepts.reduce(
    (total, concept) => (conceptHasAdCopy(adUnitCount(concept)) ? total + 1 : total),
    0,
  );
}

// =============================================================================
// THE CHAIN'S "COPY" NODE READS A DIFFERENT, STRICTER BAR THAN THE BAR ABOVE —
// ON PURPOSE — WHICH BREAKS FOR A PLATFORM THAT CAN NEVER CLEAR IT.
//
// `conceptHasAdCopy` above answers "does copy exist at all" (any `ad_units`
// row). `BriefChainStatusService.getCopyState` / the Brief-flow "Copy" node
// (`src/components/chain/BriefFlow.tsx`) and the `Ready` concept stage
// (`concept-stage.ts`) both answer a NARROWER question on purpose: "has this
// Concept's copy been ACCEPTED" — `project_tasks.current_ad_unit_id` set,
// via `select_brief_concept_copy_revision`. That distinction is real and
// load-bearing for `Ready`: accepted copy is what actually travels downstream
// to a paid delivery.
//
// A platform with `supportsCopyRevision: false` can NEVER clear the strict
// bar, structurally — not today, not once real, grounded copy has been
// written for it. That was true of LinkedIn until 2026-09-24 (Lane R3-1):
// brief-order run `226b6173-…` had already written 3 real LinkedIn
// `ad_units` rows (`source: 'pipeline'`) for 3 LinkedIn Concepts, and the
// Copy chain node read "No current copy" forever, because
// `select_brief_concept_copy_revision` refused to set `current_ad_unit_id`
// for LinkedIn at all — not "not yet accepted", a bar the platform could not
// reach BY DESIGN, which is exactly the case this function's fallback branch
// exists for. LinkedIn's `ad_copy_platforms.supports_copy_revision` is `true`
// now (verified live: brief `a26a7903-…`'s two LinkedIn Concepts both carry a
// `current_ad_unit_id` set by `select_brief_concept_copy_revision`), so for
// LinkedIn `hasAcceptedCopy` and `hasAnyAdUnit` converge to the same answer
// the strict bar already gives — the fallback below is now live ONLY for
// Google RSA, which still needs its own selection shape before it flips (see
// the registry migration's own note). The node's own "genuinely zero" vs
// "unknown" honesty rule (`BriefFlow.tsx`'s own doc comment) says a real,
// permanent zero must never stand in for "no evidence has been produced" —
// which is why the fallback stays, scoped to whatever the registry still
// marks unsupported, rather than being deleted now that LinkedIn cleared it.
//
// `conceptHasReachedCopyProgress` is that node's bar, scoped to it alone:
// still strict (accepted only) for a platform that CAN be accepted, and
// falls back to "any ad_units row" ONLY for a platform that structurally
// cannot be. `Ready`/`copyReady` (`concept-stage.ts`) does NOT read this
// function — it keeps the strict bar unconditionally, because a Concept
// whose copy cannot yet reach paid delivery is correctly not `Ready`.
// =============================================================================

export interface ConceptCopyProgressInput {
  /** `project_tasks.current_ad_unit_id !== null` for this Concept. */
  readonly hasAcceptedCopy: boolean;
  /** At least one `ad_units` row exists for this Concept, on any platform. */
  readonly hasAnyAdUnit: boolean;
  /** `ad_copy_platforms.supports_copy_revision` for this Concept's paid platform. `true` (the safe default) when the platform is unknown or unregistered — see the doc comment above `Ready`'s own strict reading. */
  readonly supportsCopyRevision: boolean;
}

/** The Copy chain node's own bar — see the block comment above for why it
 *  differs from `conceptHasAdCopy` and must never feed `Ready`/`copyReady`. */
export function conceptHasReachedCopyProgress(input: ConceptCopyProgressInput): boolean {
  if (input.hasAcceptedCopy) return true;
  if (input.supportsCopyRevision) return false;
  return input.hasAnyAdUnit;
}
